import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom"
import { GoogleOAuthProvider } from '@react-oauth/google'
import LandingPage from "./LandingPage";
import LoginForm from "./components/LoginForm"
import RegisterForm from "./components/RegisterForm"
import RequirePhone from "./components/RequirePhone"
import RequireOnboarding from "./components/RequireOnboarding"
import MarketplacePage from "./components/MarketplacePage"
import OrderReceiptPage from "./components/OrderReceiptPage"
import SeoHead from "./components/SeoHead"
import axios from './utils/axios';
import Notification from "./components/Notification";
import translations from './translations';
import { FeatureFlagProvider } from './utils/featureFlags';
import { initAnalytics, trackPageView } from './utils/analytics';
import { initMetaPixel, isMetaPixelPath, trackMetaPageView } from './utils/metaPixel';

const CompleteProfile = lazy(() => import("./components/CompleteProfile"));
const OnboardingQuiz = lazy(() => import("./components/OnboardingQuiz"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const TrialExpired = lazy(() => import("./components/TrialExpired"));
const POSv2 = lazy(() => import("./components/POSv2").then((mod) => ({ default: mod.POSInterface })));
const AdminOrdersTable = lazy(() => import("./components/adminorderstable"));
const AdminPayoutsTable = lazy(() => import("./components/adminpayoutstable"));
const CooperativeCatalog = lazy(() => import("./components/CooperativeCatalog"));
const CooperativeReviewsPage = lazy(() => import("./components/CooperativeReviewsPage"));
const CareNgoDashboardDemo = lazy(() => import("./components/CareNgoDashboardDemo"));
const BlogListPage = lazy(() => import("./components/BlogListPage"));
const BlogPostPage = lazy(() => import("./components/BlogPostPage"));
const CoopStoriesListPage = lazy(() => import("./components/CoopStoriesListPage"));
const CoopStoryPage = lazy(() => import("./components/CoopStoryPage"));
const AdminBlogManager = lazy(() => import("./components/AdminBlogManager"));
const AdminMarketplace = lazy(() => import("./components/AdminMarketplace"));
const Workspace = lazy(() => import("./components/Workspace"));

// Redirect /coop/:slug → /:slug (301-style: old links still work, canonical URL is clean)
function CoopRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}

function LegacyBlogPostRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/article/b/${slug}`} replace />;
}

function AnalyticsPageTracker() {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
    trackPageView(`${location.pathname}${location.search}`);
    if (isMetaPixelPath(location.pathname)) {
      initMetaPixel();
      trackMetaPageView();
    }
  }, [location.pathname, location.search]);

  return null;
}

function PrivatePage({ children }) {
  return (
    <>
      <SeoHead noindex title="Taaouniaty" description="" />
      {children}
    </>
  );
}

function WorkspaceGate({ children }) {
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    axios
      .get("/api/workspace/")
      .then((res) => {
        if (alive) setState(res.data?.use_workspace ? "workspace" : "dashboard");
      })
      .catch(() => {
        if (alive) setState("dashboard");
      });
    return () => {
      alive = false;
    };
  }, []);

  if (state === "loading") return <div className="shop-boot" aria-busy="true" />;
  if (state === "workspace") return <Navigate to="/workspace" replace />;
  return children;
}

// Supported languages
const supportedLanguages = ['en', 'fr', 'ar'];

function getBrowserLanguage() {
  const browserLang = navigator.language.slice(0, 2);
  return supportedLanguages.includes(browserLang) ? browserLang : 'en';
}

function App() {
  const getInitialLanguage = () => {
    const saved = localStorage.getItem('language');
    if (saved) return saved;
    // default to French for landing, fallback to browser
    const browser = getBrowserLanguage();
    return ['fr', 'ar'].includes(browser) ? browser : 'fr';
  }
  const [language, setLanguage] = useState(getInitialLanguage());
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const sessionTimeoutRef = useRef(null);
  const sessionWarningTimeoutRef = useRef(null);

  // Helper to parse JWT expiry
  function getTokenExpiry(token) {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  // Refresh access token using refresh token
  async function refreshAccessToken() {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) return false;
    try {
      const res = await fetch('/api/auth/token/refresh/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.access) {
          localStorage.setItem('access_token', data.access);
          return true;
        }
      }
    } catch {}
    return false;
  }

  // Set up session timers
  useEffect(() => {
    function setupSessionTimers() {
      if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
      if (sessionWarningTimeoutRef.current) clearTimeout(sessionWarningTimeoutRef.current);
      const access = localStorage.getItem('access_token');
      const expiry = getTokenExpiry(access);
      if (!expiry) return;
      const now = Date.now();
      const msToExpiry = expiry - now;
      if (msToExpiry <= 0) {
        setSessionExpired(true);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Clear feature flags cache
        localStorage.removeItem('feature_flags_cache');
        localStorage.removeItem('feature_flags_cache_time');
        // Dispatch session expired event
        window.dispatchEvent(new CustomEvent('sessionExpired'));
        return;
      }
      // Warn 2 minutes before expiry
      const msToWarning = msToExpiry - 2 * 60 * 1000;
      if (msToWarning > 0) {
        sessionWarningTimeoutRef.current = setTimeout(() => setSessionWarning(true), msToWarning);
      }
      sessionTimeoutRef.current = setTimeout(async () => {
        // Try to refresh token
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          setSessionWarning(false);
          setupSessionTimers();
        } else {
      setSessionExpired(true);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          // Clear feature flags cache
          localStorage.removeItem('feature_flags_cache');
          localStorage.removeItem('feature_flags_cache_time');
          // Dispatch session expired event
          window.dispatchEvent(new CustomEvent('sessionExpired'));
        }
      }, msToExpiry);
    }
    setupSessionTimers();
    // Reset timers on user activity
    const resetTimers = () => {
      setSessionWarning(false);
      setupSessionTimers();
    };
    window.addEventListener('mousemove', resetTimers);
    window.addEventListener('keydown', resetTimers);
    return () => {
      if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
      if (sessionWarningTimeoutRef.current) clearTimeout(sessionWarningTimeoutRef.current);
      window.removeEventListener('mousemove', resetTimers);
      window.removeEventListener('keydown', resetTimers);
    };
  }, []);

  const token = localStorage.getItem('access_token');
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  useEffect(() => {
    const onUnpaid = () => {
      if (window.location.pathname !== "/trial-expired") {
        window.location.assign("/trial-expired");
      }
    };
    window.addEventListener("trialExpired", onUnpaid);
    return () => window.removeEventListener("trialExpired", onUnpaid);
  }, []);

  // Persist language selection
  useEffect(() => {
    try {
      localStorage.setItem('language', language);
    } catch {}
  }, [language]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      // You may want to use useNavigate here, but it's not available in this context
      // So you can handle redirect in your components if needed
    }
  }, []);

  return (
    <>
      {sessionWarning && (
        <Notification
          show={true}
          message={
            language === 'ar'
              ? 'ستنتهي جلستك قريبًا. سيتم تمديدها تلقائيًا إذا كنت نشطًا.'
              : language === 'fr'
              ? 'Votre session va bientôt expirer. Elle sera prolongée automatiquement si vous êtes actif.'
              : 'Your session will expire soon. It will be extended automatically if you are active.'
          }
          type="warning"
          onClose={() => setSessionWarning(false)}
        />
      )}
      {sessionExpired && (
        <Notification
          show={true}
          message={
            language === 'ar'
              ? 'انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى.'
              : language === 'fr'
              ? 'Session expirée, veuillez vous reconnecter.'
              : 'Session expired, please log in again.'
          }
          type="warning"
          onClose={() => setSessionExpired(false)}
        />
      )}
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || "546404959665-f0kkf6upo6r790fjm073uob1fvq7f0rc.apps.googleusercontent.com"}>
      <FeatureFlagProvider>
        <Router>
          <AnalyticsPageTracker />
          <Suspense fallback={<div className="shop-boot" aria-busy="true" />}>
          <Routes>
            <Route path="/" element={<LandingPage language={language} setLanguage={setLanguage} />} />
            <Route path="/login" element={<PrivatePage><LoginForm language={language} setLanguage={setLanguage} translations={translations} /></PrivatePage>} />
            <Route path="/register" element={<PrivatePage><RegisterForm language={language} setLanguage={setLanguage} translations={translations} /></PrivatePage>} />
            <Route path="/complete-profile" element={<PrivatePage><CompleteProfile language={language} translations={translations} /></PrivatePage>} />
            <Route path="/onboarding" element={<PrivatePage><RequirePhone><OnboardingQuiz language={language} setLanguage={setLanguage} /></RequirePhone></PrivatePage>} />
            <Route path="/dashboard" element={<PrivatePage><RequirePhone><RequireOnboarding><WorkspaceGate><Dashboard language={language} setLanguage={setLanguage} translations={translations} /></WorkspaceGate></RequireOnboarding></RequirePhone></PrivatePage>} />
            <Route path="/workspace/*" element={<PrivatePage><RequirePhone><RequireOnboarding><Workspace language={language} translations={translations} /></RequireOnboarding></RequirePhone></PrivatePage>} />
            <Route path="/pos" element={<PrivatePage><RequirePhone><RequireOnboarding><POSv2 language={language} translations={translations[language]} setLanguage={setLanguage} /></RequireOnboarding></RequirePhone></PrivatePage>} />
            <Route path="/trial-expired" element={<PrivatePage><TrialExpired language={language} setLanguage={setLanguage} /></PrivatePage>} />
            {/* Public NGO impact demo (e.g. taaouniaty.com/care-dashboard-demo) */}
            <Route
              path="/care-dashboard-demo"
              element={<CareNgoDashboardDemo language={language} />}
            />
            {/* Public articles (SEO) — must be before /:slug */}
            <Route path="/article" element={<Navigate to="/article/blog" replace />} />
            <Route path="/article/blog" element={<BlogListPage language={language} />} />
            <Route path="/article/b/:slug" element={<BlogPostPage language={language} />} />
            <Route path="/article/cooperatives" element={<CoopStoriesListPage language={language} />} />
            <Route path="/article/c/:slug" element={<CoopStoryPage language={language} />} />
            <Route path="/blog" element={<Navigate to="/article/blog" replace />} />
            <Route path="/blog/:slug" element={<LegacyBlogPostRedirect />} />
            <Route path="/marketplace/order/:token" element={<OrderReceiptPage />} />
            <Route path="/marketplace/*" element={<MarketplacePage language={language} setLanguage={setLanguage} />} />
            {/* Private Admin Routes - Access via secret URLs */}
            <Route path="/admin/orders" element={<PrivatePage><AdminOrdersTable /></PrivatePage>} />
            <Route path="/admin/payouts" element={<PrivatePage><AdminPayoutsTable /></PrivatePage>} />
            <Route path="/admin/blog" element={<PrivatePage><AdminBlogManager /></PrivatePage>} />
            <Route path="/admin/marketplace/*" element={<PrivatePage><AdminMarketplace /></PrivatePage>} />
            {/* Public order receipt (shareable link from WhatsApp) */}
            <Route path="/receipt/:token" element={<OrderReceiptPage />} />
            {/* Coop receipt URL: /coop-slug/order/token (e.g. /breadhouse/order/abc123) */}
            <Route path="/:slug/order/:token" element={<OrderReceiptPage />} />
            {/* Public cooperative reviews page */}
            <Route path="/:slug/reviews" element={<CooperativeReviewsPage />} />
            {/* Legacy: redirect old /coop/xxx links to canonical /xxx */}
            <Route path="/coop/:slug" element={<CoopRedirect />} />
            {/* Public Cooperative Catalog (canonical URL: /cooperativename) */}
            <Route path="/:slug" element={<CooperativeCatalog language={language} setLanguage={setLanguage} />} />
          </Routes>
          </Suspense>
        </Router>
      </FeatureFlagProvider>
    </GoogleOAuthProvider>
    </>
  )
}

export default App
